import { z } from 'zod';
import { CisLevelScope } from './cis-levels.js';
import { CompliancePosture, NO_COMPLIANCE_POSTURE } from './compliance-posture.js';
import { IsoTime, ServerId, ServerRef } from './basics.js';
import { ServerGroupId } from './server.js';
import { ScaPolicyId } from './wazuh-findings.js';
/*
 * Compliance views (build plan P11.8.9): the configuration assessment's checks
 * (P11.8.6) seen through the standards Wazuh's policies tag them with. Nothing
 * new is collected: each check already carries its controls
 * (`pci_dss_v4.0: ["1.2.5", "2.2.4"]`), as the CIS benchmark maps them.
 *
 * A control **fails** on a server when any of its checks fails there, **passes**
 * when its checks that could be judged all pass, and is not applicable where
 * none could. A **score** is of checks — the passing share of the standard's
 * checks that could be judged, as Wazuh's own score is — since a control has
 * many checks and nearly every one fails somewhere on a real server. This is
 * evidence a benchmark gives, not an audit: a standard asks for far more than
 * any server's configuration shows.
 */
/** The standards Wazuh's CIS policies tag, the four the plan names first. */
export const COMPLIANCE_STANDARDS = [
    { id: 'pci_dss_v4.0', label: 'PCI DSS 4.0' },
    { id: 'cis', label: 'CIS Benchmark' },
    { id: 'nist_sp_800-53', label: 'NIST SP 800-53' },
    { id: 'hipaa', label: 'HIPAA' },
    { id: 'pci_dss_v3.2.1', label: 'PCI DSS 3.2.1' },
    { id: 'iso_27001-2013', label: 'ISO/IEC 27001:2013' },
    { id: 'soc_2', label: 'SOC 2' },
    { id: 'cmmc_v2.0', label: 'CMMC 2.0' },
    { id: 'cis_csc_v8', label: 'CIS Controls v8' },
];
export const ComplianceStandard = z.enum([
    'pci_dss_v4.0',
    'cis',
    'nist_sp_800-53',
    'hipaa',
    'pci_dss_v3.2.1',
    'iso_27001-2013',
    'soc_2',
    'cmmc_v2.0',
    'cis_csc_v8',
]);
export const complianceLabel = (standard) => COMPLIANCE_STANDARDS.find((each) => each.id === standard)?.label ?? standard;
/**
 * Which servers a view is of: one server, or a group's, or (neither) all. Both is
 * the server, narrowed by the group it is in.
 */
export const ComplianceScope = z
    .object({ server: ServerId.optional(), group: ServerGroupId.optional() })
    .strict();
/**
 * What a view of the assessment is of: which servers, and which CIS levels' checks
 * (P11.21). Decisions and refreshes are of servers only, so they take a
 * `ComplianceScope`; only the readings are scoped by level.
 */
export const ComplianceView = ComplianceScope.extend({ level: CisLevelScope.optional() });
export const COMPLIANCE_LIMITS = {
    /** Servers named per control or check; the count says how many in all. */
    serversNamed: 20,
    fixFirst: 50,
    controls: 2000,
    servers: 5000,
};
/** Passing and failing controls, and the score of the checks (null with nothing judged). */
const Tally = z.object({
    passing: z.number().int().nonnegative(),
    failing: z.number().int().nonnegative(),
    /** Per cent, the passing share of the checks that could be judged. */
    score: z.number().int().min(0).max(100).nullable(),
});
export const ComplianceSummaryItem = z.object({
    standard: ComplianceStandard,
    label: z.string().max(100),
    /** Per cent of every server's checks of it that could be judged, passing. */
    score: z.number().int().min(0).max(100).nullable(),
    /** Controls failing on at least one server, and those passing on every server judged. */
    controls: z.object({
        passing: z.number().int().nonnegative(),
        failing: z.number().int().nonnegative(),
    }),
    servers: z.object({
        assessed: z.number().int().nonnegative(),
        /** Servers failing at least one of its controls. */
        failing: z.number().int().nonnegative(),
    }),
    /** What the failing checks are and who decides (P11.20), with the trend. */
    posture: CompliancePosture.default(NO_COMPLIANCE_POSTURE),
});
/** How often the API reads each server's findings from the Wazuh manager. */
export const WAZUH_FINDINGS_READ_EVERY_SECONDS = 600;
/**
 * Where the numbers on the page are in their journey from a server's scan (P11.19):
 * the fixes applied, the scan the Wazuh agent ran afterwards, and Bartizo reading it
 * from the manager (every `readEverySeconds`).
 */
export const ComplianceStatus = z.object({
    /** When Bartizo last read the shown servers' findings from the manager; null if never. */
    readAt: IsoTime.nullable(),
    /** The newest finished `apply_compliance_fixes` on the shown servers. */
    lastApply: z.object({ at: IsoTime, failed: z.boolean() }).nullable(),
    /** How often the API reads the manager: the longest a finished scan waits to show. */
    readEverySeconds: z.number().int().positive(),
});
/** What the page says before the API reports any of it. */
export const NO_COMPLIANCE_STATUS = {
    readAt: null,
    lastApply: null,
    readEverySeconds: WAZUH_FINDINGS_READ_EVERY_SECONDS,
};
/** `POST /v1/compliance/refresh`: what the read of the shown servers did. */
export const ComplianceRefreshResult = z.object({
    /** Servers asked about; those read within the last few seconds are left alone. */
    servers: z.number().int().nonnegative(),
    /** Policies whose newer scan was stored. */
    policiesRead: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
});
/** `GET /v1/compliance`. */
export const ComplianceSummary = z.object({
    /** Each standard some assessed check is tagged with, in `COMPLIANCE_STANDARDS`' order. */
    standards: z.array(ComplianceSummaryItem).max(COMPLIANCE_STANDARDS.length),
    /** Servers with a configuration assessment. */
    servers: z.number().int().nonnegative(),
    /** The newest assessment read. */
    scannedAt: IsoTime.nullable(),
    status: ComplianceStatus.default(NO_COMPLIANCE_STATUS),
    /**
     * How many of the shown servers' CIS checks have a known level (P11.21). A check
     * whose level is not known stays in the Level 1 and Level 2 scopes, so this is
     * what says how far a scoped score can be trusted to be scoped.
     */
    levels: z
        .object({ known: z.number().int().nonnegative(), total: z.number().int().nonnegative() })
        .default({ known: 0, total: 0 }),
});
export const ComplianceControl = z.object({
    /** `1.2.5`; for CIS, a section of one benchmark, named with its policy. */
    control: z.string().min(1).max(64),
    policyId: ScaPolicyId.nullable(),
    status: z.enum(['failing', 'passing', 'not_applicable']),
    failingServers: z.array(ServerRef).max(COMPLIANCE_LIMITS.serversNamed),
    failingServerCount: z.number().int().nonnegative(),
    passingServerCount: z.number().int().nonnegative(),
    /** How many checks of the policies map to it. */
    checks: z.number().int().nonnegative(),
    /** What its checks are about, in the benchmark's words: failing ones first, at most three. */
    titles: z.array(z.string().max(300)).max(3).default([]),
});
/** One failing check, ranked by what fixing it would fix. */
export const ComplianceFix = z.object({
    policyId: ScaPolicyId,
    checkId: z.number().int().nonnegative(),
    title: z.string().max(300),
    /** Why it matters, as the benchmark says. */
    rationale: z.string().max(8000).nullable().default(null),
    remediation: z.string().max(8000).nullable(),
    servers: z.array(ServerRef).max(COMPLIANCE_LIMITS.serversNamed),
    serverCount: z.number().int().nonnegative(),
    /** The standard's controls it maps to. */
    controls: z.array(z.string().max(64)).max(100),
});
/** `GET /v1/compliance/:standard`. */
export const ComplianceStandardView = z.object({
    standard: ComplianceStandard,
    label: z.string().max(100),
    score: z.number().int().min(0).max(100).nullable(),
    /** Failing first (most servers first), then passing, then not applicable; by control. */
    controls: z.array(ComplianceControl).max(COMPLIANCE_LIMITS.controls),
    /** What to fix first: servers failing it × controls it maps to, most first. */
    fixFirst: z.array(ComplianceFix).max(COMPLIANCE_LIMITS.fixFirst),
    /** Each assessed server's score for the standard, lowest first. */
    servers: z.array(ServerRef.extend(Tally.shape)).max(COMPLIANCE_LIMITS.servers),
});
/** One server's score per standard, on its Hardening tab. */
export const ServerComplianceScore = z
    .object({ standard: ComplianceStandard, label: z.string().max(100) })
    .extend(Tally.shape);
/** Controls in their natural order: `1.2.10` after `1.2.9`, `AC-6` after `AC-5`. */
export const compareControls = (a, b) => a.localeCompare(b, 'en', { numeric: true });
/** One check behind a control: what it asks, why, how to fix it, and where it fails. */
export const ComplianceCheck = z.object({
    policyId: ScaPolicyId,
    checkId: z.number().int().nonnegative(),
    title: z.string().max(300),
    rationale: z.string().max(8000).nullable(),
    remediation: z.string().max(8000).nullable(),
    status: z.enum(['failing', 'passing', 'not_applicable']),
    failingServers: z.array(ServerRef).max(COMPLIANCE_LIMITS.serversNamed),
    failingServerCount: z.number().int().nonnegative(),
});
/** `GET /v1/compliance/:standard/checks?control=…`: the checks mapped to one control. */
export const ComplianceControlChecks = z.object({
    control: z.string().max(64),
    checks: z.array(ComplianceCheck).max(200),
});
export const ComplianceControlQuery = ComplianceView.extend({
    control: z.string().min(1).max(64),
    /** A CIS section belongs to one benchmark. */
    policy: ScaPolicyId.optional(),
});
//# sourceMappingURL=compliance.js.map